/**
 * Billing API + TanStack Query hooks — Phase 7.
 *
 * Mirrors @features/bookings/api/bookings.ts:
 *   - typed via the generated OpenAPI schema (`@api/index`)
 *   - centralised query-key factory
 *   - mutations invalidate the list cache AND adjacent caches that may
 *     have shifted (entitlements after payment → paid; ledger after
 *     invoice issue / payment record / payment void).
 *
 * All amounts are integers in the tenant's minor currency unit (paise
 * for INR). UI helpers in {@link "@features/invoices/config/ui"} format
 * for display.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

// ---------------------------------------------------------------------------
// Type re-exports — single source of truth = the generated OpenAPI schema
// ---------------------------------------------------------------------------

// 11F.7 additions are declared BY HAND on top of the generated schema
// rather than by regenerating `shared/api-types/index.ts`. That file is
// known-stale against the live backend and regenerating it surfaces
// pre-existing tsc errors across catalog/packages/people (found and flagged
// in 11F.4, deliberately out of scope here). Same precedent and rationale as
// `@features/attendance/api/leaves.ts`. When the shared types are eventually
// regenerated, these intersections become no-ops and can be deleted.

export type InvoiceLineItemOut = components['schemas']['InvoiceLineItemOut'] & {
  /** Denormalised subject display name (11F.7). */
  subject_name?: string | null;
};

export type InvoiceResponse = Omit<
  components['schemas']['InvoiceResponse'],
  'line_items'
> & {
  /** Denormalised student display name (11F.7); '' when unresolvable. */
  student_name?: string;
  line_items: InvoiceLineItemOut[];
};

export type InvoiceListResponse = Omit<
  components['schemas']['InvoiceListResponse'],
  'items'
> & {
  items: InvoiceResponse[];
  /** Present only in paged mode (11F.7); null in legacy cursor mode. */
  page?: number | null;
  page_size?: number | null;
  pages?: number | null;
};

export type InvoiceCreate = components['schemas']['InvoiceCreate'];
export type InvoiceUpdate = components['schemas']['InvoiceUpdate'];
export type InvoiceVoidRequest = components['schemas']['InvoiceVoidRequest'];
export type InvoiceLineItemIn = components['schemas']['InvoiceLineItemIn'];
export type InvoiceStatus = InvoiceResponse['status'];

export type PaymentResponse = components['schemas']['PaymentResponse'] & {
  /** Denormalised for the Payments list's Student column (11F.7). */
  student_name?: string;
  /** Denormalised issued number of the paid invoice (11F.7). */
  invoice_number?: string | null;
  /** Sum of active refunds against this payment (11F.7), minor units. */
  refunded_amount?: number;
};

export type PaymentListResponse = Omit<
  components['schemas']['PaymentListResponse'],
  'items'
> & {
  items: PaymentResponse[];
  page?: number | null;
  page_size?: number | null;
  pages?: number | null;
};

export type PaymentCreate = components['schemas']['PaymentCreate'];
export type PaymentVoidRequest = components['schemas']['PaymentVoidRequest'];
export type PaymentMethod = PaymentResponse['method'];
export type PaymentStatus = PaymentResponse['status'];

/** `GET /v1/invoices/summary` (11F.7) — the three cards above the list. */
export interface InvoicesSummaryBucket {
  count: number;
  amount: number;
}

export interface InvoicesSummaryResponse {
  invoiced: InvoicesSummaryBucket;
  collected: InvoicesSummaryBucket;
  due: InvoicesSummaryBucket;
}

/** Refunds (11F.7) — distinct from voiding a payment. */
export interface RefundResponse {
  id: string;
  payment_id: string;
  student_id: string;
  amount: number;
  refund_date: string;
  method: PaymentMethod;
  reason: string;
  reference_number: string | null;
  recorded_by: string | null;
  recorded_at: string;
  created_at: string;
  updated_at: string;
}

export interface RefundCreate {
  amount: number;
  refund_date: string;
  method: PaymentMethod;
  reason: string;
  reference_number?: string;
}

export interface RefundListResponse {
  items: RefundResponse[];
  total: number;
}

export type LedgerResponse = components['schemas']['LedgerResponse'];
export type LedgerEntryResponse = components['schemas']['LedgerEntryResponse'];
export type StudentBalanceResponse = components['schemas']['StudentBalanceResponse'];

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export interface InvoicesFilters {
  studentId?: string;
  status?: InvoiceStatus | '';
  from?: string; // YYYY-MM-DD
  to?: string;
  limit?: number;
}

export interface PaymentsFilters {
  invoiceId?: string;
  studentId?: string;
  status?: PaymentStatus | '';
  from?: string;
  to?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const invoicesKeys = {
  all: ['invoices'] as const,
  list: (filters: InvoicesFilters) => ['invoices', 'list', filters] as const,
  detail: (id: string) => ['invoices', 'detail', id] as const,
  summary: (from?: string, to?: string) => ['invoices', 'summary', from, to] as const,
};

export const paymentsKeys = {
  all: ['payments'] as const,
  list: (filters: PaymentsFilters) => ['payments', 'list', filters] as const,
  detail: (id: string) => ['payments', 'detail', id] as const,
  refunds: (paymentId: string) => ['payments', 'refunds', paymentId] as const,
};

export const ledgerKeys = {
  all: ['ledger'] as const,
  student: (studentId: string) => ['ledger', 'student', studentId] as const,
  balance: (studentId: string) => ['ledger', 'balance', studentId] as const,
};

// ---------------------------------------------------------------------------
// REST callers
// ---------------------------------------------------------------------------

function invoiceQuery(filters: InvoicesFilters): Record<string, string | number | undefined> {
  return {
    student_id: filters.studentId || undefined,
    status: filters.status || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    limit: filters.limit ?? undefined,
  };
}

function paymentQuery(filters: PaymentsFilters): Record<string, string | number | undefined> {
  return {
    invoice_id: filters.invoiceId || undefined,
    student_id: filters.studentId || undefined,
    status: filters.status || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    limit: filters.limit ?? undefined,
  };
}

// ----- Invoices -------------------------------------------------------------

export async function listInvoices(filters: InvoicesFilters): Promise<InvoiceListResponse> {
  return api.get<InvoiceListResponse>('/v1/invoices', { query: invoiceQuery(filters) });
}

export async function getInvoice(id: string): Promise<InvoiceResponse> {
  return api.get<InvoiceResponse>(`/v1/invoices/${id}`);
}

export async function createInvoice(body: InvoiceCreate): Promise<InvoiceResponse> {
  return api.post<InvoiceResponse>('/v1/invoices', body);
}

export async function updateInvoice(id: string, body: InvoiceUpdate): Promise<InvoiceResponse> {
  return api.patch<InvoiceResponse>(`/v1/invoices/${id}`, body);
}

export async function issueInvoice(id: string): Promise<InvoiceResponse> {
  return api.post<InvoiceResponse>(`/v1/invoices/${id}/issue`);
}

export async function voidInvoice(id: string, body: InvoiceVoidRequest): Promise<InvoiceResponse> {
  return api.post<InvoiceResponse>(`/v1/invoices/${id}/void`, body);
}

/**
 * Fetch the rendered PDF as a Blob. Caller is responsible for converting
 * to an object URL (for download or iframe preview) and revoking it.
 */
export async function fetchInvoicePdf(id: string): Promise<Blob> {
  return api.get<Blob>(`/v1/invoices/${id}/pdf`);
}

// ----- Payments -------------------------------------------------------------

export async function listPayments(filters: PaymentsFilters): Promise<PaymentListResponse> {
  return api.get<PaymentListResponse>('/v1/payments', { query: paymentQuery(filters) });
}

export async function getPayment(id: string): Promise<PaymentResponse> {
  return api.get<PaymentResponse>(`/v1/payments/${id}`);
}

export async function recordPayment(body: PaymentCreate): Promise<PaymentResponse> {
  return api.post<PaymentResponse>('/v1/payments', body);
}

export async function voidPayment(id: string, body: PaymentVoidRequest): Promise<PaymentResponse> {
  return api.post<PaymentResponse>(`/v1/payments/${id}/void`, body);
}

export async function uploadReceipt(id: string, file: File): Promise<PaymentResponse> {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<PaymentResponse>(`/v1/payments/${id}/receipt`, fd, { multipart: true });
}

// 11E.8 §7 — additive proof attachments (e.g. cash-payment screenshots),
// distinct from the receipt_url mechanism above. `attachments` isn't in the
// generated PaymentResponse type yet (shared/api-types predates this field,
// same known-stale situation @features/attendance/api/leaves.ts documents
// for its own hand-declared types) — widened locally rather than
// regenerating the whole types file.
export interface PaymentAttachmentRef {
  storage_path: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  uploaded_by_uid: string;
}

export type PaymentWithAttachments = PaymentResponse & { attachments?: PaymentAttachmentRef[] };

export interface AttachmentUrlResponse {
  download_url: string;
  expires_at: string;
}

export async function uploadPaymentAttachment(
  id: string,
  file: File,
): Promise<PaymentWithAttachments> {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<PaymentWithAttachments>(`/v1/payments/${id}/attachments`, fd, {
    multipart: true,
  });
}

export async function getPaymentAttachmentUrl(
  id: string,
  filename: string,
): Promise<AttachmentUrlResponse> {
  return api.get<AttachmentUrlResponse>(
    `/v1/payments/${id}/attachments/${encodeURIComponent(filename)}/url`,
  );
}

// ----- Ledger ---------------------------------------------------------------

export async function fetchStudentLedger(
  studentId: string,
  limit = 100,
): Promise<LedgerResponse> {
  return api.get<LedgerResponse>(`/v1/students/${studentId}/ledger`, {
    query: { limit },
  });
}

export async function fetchStudentBalance(studentId: string): Promise<StudentBalanceResponse> {
  return api.get<StudentBalanceResponse>(`/v1/students/${studentId}/balance`);
}

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

export function useInvoices(filters: InvoicesFilters = {}) {
  return useQuery({
    queryKey: invoicesKeys.list(filters),
    queryFn: () => listInvoices(filters),
    staleTime: 30_000,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: invoicesKeys.detail(id ?? '__none__'),
    queryFn: () => getInvoice(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function usePayments(filters: PaymentsFilters = {}) {
  return useQuery({
    queryKey: paymentsKeys.list(filters),
    queryFn: () => listPayments(filters),
    staleTime: 30_000,
  });
}

export function useStudentLedger(studentId: string | undefined, limit = 100) {
  return useQuery({
    queryKey: ledgerKeys.student(studentId ?? '__none__'),
    queryFn: () => fetchStudentLedger(studentId as string, limit),
    enabled: Boolean(studentId),
    staleTime: 15_000,
  });
}

export function useStudentBalance(studentId: string | undefined) {
  return useQuery({
    queryKey: ledgerKeys.balance(studentId ?? '__none__'),
    queryFn: () => fetchStudentBalance(studentId as string),
    enabled: Boolean(studentId),
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// Cache invalidation
// ---------------------------------------------------------------------------

/**
 * Scoped invalidation helper.
 *
 * Each mutation tells this helper what kind of state it just changed —
 * the helper invalidates only the relevant query keys. This is much
 * lighter than blasting every billing-shaped query on every mutation
 * (which was the previous shape; see Opus-review LOW #16).
 *
 * - ``invoice``       → invoice list + the one detail key (if known).
 * - ``payment``       → payment list + invoice list (status may have flipped)
 *                       + ledger / balance for the affected student.
 * - ``entitlements``  → catalog entitlements (set when a payment fully pays
 *                       an invoice that carries package line items).
 */
type InvalidationScope = {
  invoiceId?: string;
  studentId?: string;
  touchesPayments?: boolean;
  touchesEntitlements?: boolean;
  touchesLedger?: boolean;
};

function useInvalidateBilling() {
  const qc = useQueryClient();
  return (scope: InvalidationScope = {}) => {
    // Invoice list is always cheap to invalidate and almost always stale
    // after any billing mutation.
    qc.invalidateQueries({ queryKey: invoicesKeys.all });
    if (scope.invoiceId) {
      qc.invalidateQueries({ queryKey: invoicesKeys.detail(scope.invoiceId) });
    }
    if (scope.touchesPayments) {
      qc.invalidateQueries({ queryKey: paymentsKeys.all });
    }
    if (scope.touchesLedger && scope.studentId) {
      qc.invalidateQueries({ queryKey: ledgerKeys.student(scope.studentId) });
      qc.invalidateQueries({ queryKey: ledgerKeys.balance(scope.studentId) });
    } else if (scope.touchesLedger) {
      qc.invalidateQueries({ queryKey: ledgerKeys.all });
    }
    if (scope.touchesEntitlements) {
      qc.invalidateQueries({ queryKey: ['catalog', 'entitlements'] });
    }
  };
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateInvoice() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: createInvoice,
    onSuccess: (data) =>
      invalidate({ invoiceId: data.id, studentId: data.student_id }),
  });
}

export function useUpdateInvoice() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InvoiceUpdate }) => updateInvoice(id, body),
    onSuccess: (data) =>
      invalidate({ invoiceId: data.id, studentId: data.student_id }),
  });
}

export function useIssueInvoice() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: issueInvoice,
    onSuccess: (data) =>
      invalidate({
        invoiceId: data.id,
        studentId: data.student_id,
        touchesLedger: true,
      }),
  });
}

export function useVoidInvoice() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InvoiceVoidRequest }) => voidInvoice(id, body),
    onSuccess: (data) =>
      invalidate({
        invoiceId: data.id,
        studentId: data.student_id,
        touchesLedger: true,
      }),
  });
}

export function useRecordPayment() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: recordPayment,
    onSuccess: (data) =>
      invalidate({
        invoiceId: data.invoice_id ?? undefined,
        studentId: data.student_id,
        touchesPayments: true,
        touchesLedger: true,
        // A payment that clears an invoice with package line items spawns
        // entitlements as an after-commit side effect — refresh the catalog
        // entitlements cache so any open student-detail panel updates.
        touchesEntitlements: (data.entitlement_ids ?? []).length > 0,
      }),
  });
}

export function useVoidPayment() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PaymentVoidRequest }) => voidPayment(id, body),
    onSuccess: (data) =>
      invalidate({
        invoiceId: data.invoice_id ?? undefined,
        studentId: data.student_id,
        touchesPayments: true,
        touchesLedger: true,
        touchesEntitlements: (data.entitlement_ids ?? []).length > 0,
      }),
  });
}

export function useUploadReceipt() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadReceipt(id, file),
    onSuccess: (data) =>
      invalidate({
        invoiceId: data.invoice_id ?? undefined,
        studentId: data.student_id,
        touchesPayments: true,
      }),
  });
}


// ---------------------------------------------------------------------------
// 11F.7 — paged list contract, summary cards, refunds
//
// The paged calls hit the SAME endpoints as the cursor-mode helpers above;
// passing `page` is what switches the backend into page-number mode (exact
// total, prefix search, sortable columns). Kept as separate functions so the
// existing cursor-mode callers (StudentBillingTab, the teacher fan-out)
// cannot accidentally change behaviour.
// ---------------------------------------------------------------------------

export interface PagedListParams {
  page: number;
  page_size: number;
  status?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  from?: string;
  to?: string;
}

export interface PagedPaymentListParams extends PagedListParams {
  invoice_id?: string;
}

export async function listInvoicesPaged(
  params: PagedListParams,
): Promise<InvoiceListResponse> {
  return api.get<InvoiceListResponse>('/v1/invoices', {
    query: params as unknown as Record<string, string | number | undefined>,
  });
}

export async function listPaymentsPaged(
  params: PagedPaymentListParams,
): Promise<PaymentListResponse> {
  return api.get<PaymentListResponse>('/v1/payments', {
    query: params as unknown as Record<string, string | number | undefined>,
  });
}

export async function fetchInvoicesSummary(
  from?: string,
  to?: string,
): Promise<InvoicesSummaryResponse> {
  return api.get<InvoicesSummaryResponse>('/v1/invoices/summary', {
    query: { from, to },
  });
}

export function useInvoicesSummary(from?: string, to?: string, enabled = true) {
  return useQuery({
    queryKey: invoicesKeys.summary(from, to),
    queryFn: () => fetchInvoicesSummary(from, to),
    staleTime: 30_000,
    enabled,
  });
}

// ----- Refunds --------------------------------------------------------------

export async function listRefunds(paymentId: string): Promise<RefundListResponse> {
  return api.get<RefundListResponse>(`/v1/payments/${paymentId}/refunds`);
}

export async function recordRefund(
  paymentId: string,
  body: RefundCreate,
): Promise<RefundResponse> {
  return api.post<RefundResponse>(`/v1/payments/${paymentId}/refunds`, body);
}

export function usePaymentRefunds(paymentId: string | undefined) {
  return useQuery({
    queryKey: paymentsKeys.refunds(paymentId ?? '__none__'),
    queryFn: () => listRefunds(paymentId as string),
    enabled: Boolean(paymentId),
    staleTime: 30_000,
  });
}

export function useRecordRefund() {
  const invalidate = useInvalidateBilling();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, body }: { paymentId: string; body: RefundCreate }) =>
      recordRefund(paymentId, body),
    onSuccess: (data) => {
      // A refund moves the student's balance and the payment's
      // refunded_amount roll-up, so both the ledger and the payment caches
      // are stale — not just the refund list.
      void qc.invalidateQueries({ queryKey: paymentsKeys.refunds(data.payment_id) });
      invalidate({
        studentId: data.student_id,
        touchesPayments: true,
        touchesLedger: true,
      });
    },
  });
}
